import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { PdfFileDropzone } from "@/components/file-upload/pdf-file-dropzone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PdfBuilderEmbed } from "./pdf-builder-embed";
import type { BuilderEmbed } from "./pdf-builder-embed";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import type { RouterOutputs } from "@/trpc";

type CreatedForm =
  RouterOutputs["organizationForm"]["createPdfFormDraft"]["form"];

// ─── Step definitions ──────────────────────────────────────────────────────

type Step =
  | { kind: "upload" }
  | { kind: "builder"; form: CreatedForm; embed: BuilderEmbed }
  | { kind: "no-builder"; form: CreatedForm }
  | { kind: "publishing"; form: CreatedForm }
  | { kind: "done" };

// ─── Upload-step form schema ───────────────────────────────────────────────

const uploadSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  description: z.string().max(2000).optional(),
});

type UploadFormValues = z.infer<typeof uploadSchema>;

// ─── Main dialog ───────────────────────────────────────────────────────────

interface PdfFormUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationBusinessUnitId: string;
}

export function PdfFormUploadDialog({
  open,
  onOpenChange,
  organizationBusinessUnitId,
}: PdfFormUploadDialogProps) {
  const trpc = useTRPC();
  const [step, setStep] = useState<Step>({ kind: "upload" });
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  // ── Upload step form ──
  const uploadForm = useForm<UploadFormValues>({
    resolver: zodResolver(uploadSchema),
    defaultValues: { name: "", description: "" },
  });

  // ── Mutations ──

  const createDraftMutation = useMutation(
    trpc.organizationForm.createPdfFormDraft.mutationOptions({
      onError: (err) => toast.error(err.message || "Failed to create PDF form"),
    }),
  );

  const syncFieldsMutation = useMutation(
    trpc.organizationForm.syncPdfFields.mutationOptions(),
  );

  const publishMutation = useMutation(
    trpc.organizationForm.publishPdfForm.mutationOptions(),
  );

  // ── Handlers ──

  const handleClose = () => {
    onOpenChange(false);
    // Reset after animation
    setTimeout(() => {
      setStep({ kind: "upload" });
      setPdfFile(null);
      uploadForm.reset();
    }, 300);
  };

  const handleUploadSubmit = async (values: UploadFormValues) => {
    if (!pdfFile) {
      uploadForm.setError("root", { message: "Please select a PDF file" });
      return;
    }

    // Read PDF as base64
    const pdfBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the data URL prefix to get raw base64
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfFile);
    });

    const result = await createDraftMutation.mutateAsync({
      organizationBusinessUnitId,
      name: values.name,
      description: values.description ?? null,
      fileName: pdfFile.name,
      pdfBase64,
    });

    if (result.builderEmbed) {
      setStep({
        kind: "builder",
        form: result.form,
        embed: result.builderEmbed,
      });
    } else {
      // No embedded builder — show instructions and a manual sync button.
      setStep({ kind: "no-builder", form: result.form });
    }
  };

  const handleImportAndPublish = async (
    form: CreatedForm,
    previousStep: Step,
  ) => {
    setStep({ kind: "publishing", form });

    let synced;
    try {
      synced = await syncFieldsMutation.mutateAsync({
        organizationBusinessUnitId,
        formId: form.id,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to sync fields";
      toast.error(message);
      setStep(previousStep);
      return;
    }

    const fieldCount = (synced?.fields ?? []).length;
    if (fieldCount === 0) {
      toast.error(
        "No fields found. Add at least one field to the PDF in the builder, then try again.",
      );
      setStep(previousStep);
      return;
    }

    try {
      await publishMutation.mutateAsync({
        organizationBusinessUnitId,
        formId: form.id,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to publish form";
      toast.error(message);
      setStep(previousStep);
      return;
    }

    await queryClient.invalidateQueries(
      trpc.organizationForm.listForms.queryOptions({
        organizationBusinessUnitId,
      }),
    );
    toast.success("PDF form published");
    setStep({ kind: "done" });
  };

  // ── Render helpers ──

  const isBusy =
    createDraftMutation.isPending ||
    syncFieldsMutation.isPending ||
    publishMutation.isPending;

  function renderContent() {
    switch (step.kind) {
      case "upload":
        return (
          <Form {...uploadForm}>
            <form
              onSubmit={uploadForm.handleSubmit(handleUploadSubmit)}
              className="space-y-4"
            >
              <FormField
                control={uploadForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Form Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. I-9 Employment Eligibility"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={uploadForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe what this PDF form is for..."
                        rows={2}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-1.5">
                <Label>Blank PDF Template</Label>
                <PdfFileDropzone
                  file={pdfFile}
                  onFile={(f) => {
                    setPdfFile(f ?? null);
                    uploadForm.clearErrors("root");
                  }}
                />
                {uploadForm.formState.errors.root && (
                  <p className="text-xs text-red-500">
                    {uploadForm.formState.errors.root.message}
                  </p>
                )}
              </div>
              {createDraftMutation.error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {createDraftMutation.error.message}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isBusy || !pdfFile}>
                  {createDraftMutation.isPending ? (
                    <>
                      <Loader2 size={14} className="mr-1.5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    "Continue to Field Mapping"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        );

      case "builder":
        return (
          <div className="flex flex-col flex-1 min-h-0 gap-4">
            <p className="text-sm text-gray-600 shrink-0">
              Map your PDF fields below. When you&apos;re done, click the{" "}
              <strong>Done — Import Fields</strong> button to import them.
            </p>
            <div className="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200">
              <PdfBuilderEmbed embed={step.embed} />
            </div>
            <DialogFooter className="shrink-0">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleImportAndPublish(step.form, step)}
                disabled={isBusy}
              >
                {isBusy ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  "Done — Import Fields"
                )}
              </Button>
            </DialogFooter>
          </div>
        );

      case "no-builder":
        return (
          <div className="space-y-4">
            <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              Map the fields in DocuSeal, then click{" "}
              <strong>Sync Fields</strong> to import them.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleImportAndPublish(step.form, step)}
                disabled={isBusy}
              >
                {isBusy ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  "Sync Fields"
                )}
              </Button>
            </DialogFooter>
          </div>
        );

      case "publishing":
        return (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-500">
              Importing fields and publishing...
            </p>
          </div>
        );

      case "done":
        return (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="text-base font-medium text-gray-800">
              PDF form published!
            </p>
          </div>
        );
    }
  }

  function getTitle() {
    switch (step.kind) {
      case "upload":
        return "Upload PDF Template";
      case "builder":
      case "no-builder":
      case "publishing":
        return "Map PDF Fields";
      case "done":
        return "Done";
    }
  }

  function getDescription() {
    switch (step.kind) {
      case "upload":
        return "Upload the blank PDF that providers will fill out.";
      case "builder":
        return "Use the visual editor to map each PDF field to a form field.";
      case "no-builder":
        return "Open DocuSeal to map fields, then sync them here.";
      case "publishing":
      case "done":
        return null;
    }
  }

  const description = getDescription();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className={
          step.kind === "builder"
            ? "h-[90vh] flex flex-col overflow-hidden"
            : "w-[calc(100vw-2rem)] max-w-xl sm:max-h-[90vh] overflow-y-auto"
        }
        // Inline style beats the global `.dialog-content { max-width: 1000px }`.
        style={
          step.kind === "builder"
            ? { width: "90vw", maxWidth: "none" }
            : undefined
        }
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{getTitle()}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
